import type { Product } from '../api/admin';

export type EligibleGender = 'Male' | 'Female';

/**
 * Resolves whether a product is restricted to a gender.
 * Returns null when the product is open to all genders.
 *
 * Priority:
 * 1. Explicit product.eligibleGender
 * 2. formConfig.eligibility.gender / formConfig.eligibleGender
 * 3. Heuristics from product name / description (e.g. "Women's Loan")
 */
export function resolveProductEligibleGender(product: Product | null | undefined): EligibleGender | null {
  if (!product) return null;

  const fromExplicit = normalizeGender(
    product.eligibleGender ||
      (product.formConfig as any)?.eligibility?.gender ||
      (product.formConfig as any)?.eligibleGender
  );
  if (fromExplicit === 'ALL') return null;
  if (fromExplicit) return fromExplicit;

  const text = [
    product.productName,
    product.shortName,
    product.otherDetails,
    product.interestRateDescription,
    product.charges,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!text.trim()) return null;

  // Explicitly open to everyone — do not restrict
  if (/\b(all genders?|open to all|men and women|women and men|male and female|female and male)\b/.test(text)) {
    return null;
  }

  if (
    /\b(for women|women'?s?\s+only|women'?s?\s+loan|woman\s+entrepreneur|ladies\s+only|female\s+only|women\s+empowerment|women\s+in\s+business|women'?s?\s+fund|women'?s?\s+sme|women'?s?\s+micro|gender\s*[:\-]?\s*female|eligible\s*[:\-]?\s*female)\b/.test(
      text
    ) ||
    /\b(wef|wel|wif)\b/.test(text)
  ) {
    return 'Female';
  }

  // Name/description mentions women/lady but not men → women-oriented
  if (/\bwomen\b|\bwoman\b|\blad(y|ies)\b|women/.test(text) && !/\b(men|male|gentleman)\b/.test(text)) {
    return 'Female';
  }

  if (
    /\b(for men|men'?s?\s+only|men'?s?\s+loan|male\s+only|gentlemen\s+only|gender\s*[:\-]?\s*male|eligible\s*[:\-]?\s*male)\b/.test(
      text
    )
  ) {
    return 'Male';
  }

  return null;
}

/**
 * Whether the applicant may apply for the product.
 * Unrestricted products are always eligible.
 */
export function isApplicantEligibleForProduct(
  applicantGender: string | null | undefined,
  product: Product | null | undefined
): boolean {
  const required = resolveProductEligibleGender(product);
  if (!required) return true;
  const applicant = normalizeGender(applicantGender);
  if (!applicant || applicant === 'ALL') return false;
  return applicant === required;
}

/** Hide gender-incompatible products once applicant gender is known. */
export function filterProductsForApplicantGender(
  products: Product[],
  applicantGender: string | null | undefined
): Product[] {
  const applicant = normalizeGender(applicantGender);
  if (!applicant || applicant === 'ALL') {
    return products;
  }
  return products.filter((p) => isApplicantEligibleForProduct(applicant, p));
}

export function productGenderRestrictionMessage(product: Product | null | undefined): string | null {
  const required = resolveProductEligibleGender(product);
  if (!required) return null;
  return required === 'Female'
    ? 'This product is for women applicants only'
    : 'This product is for male applicants only';
}

/**
 * Tenure options (months) allowed for a product.
 * Uses min/max tenure from the product when available.
 */
export function getProductTenureOptions(product: Product | null | undefined): string[] {
  const defaults = [3, 6, 12, 24, 36];
  if (!product) return defaults.map(String);

  const max =
    Number(product.maxLoanTenureMonths ?? product.loanTenureMonths) ||
    Math.max(...defaults);
  const min = Number(product.minLoanTenureMonths) || 1;

  const candidates = new Set<number>();
  [3, 6, 9, 12, 15, 18, 24, 30, 36, 48, 60].forEach((m) => {
    if (m >= min && m <= max) candidates.add(m);
  });
  if (max >= min) candidates.add(max);
  if (min > 1 && min <= max) candidates.add(min);

  if (candidates.size === 0) {
    return defaults.filter((m) => m <= max).map(String);
  }
  return Array.from(candidates)
    .sort((a, b) => a - b)
    .map(String);
}

function normalizeGender(value: unknown): EligibleGender | 'ALL' | null {
  if (value == null) return null;
  const g = String(value).trim().toLowerCase();
  if (!g) return null;
  if (['all', 'any', 'none', 'both', 'unisex'].includes(g)) return 'ALL';
  if (['female', 'f', 'woman', 'women', 'lady', 'ladies'].includes(g)) return 'Female';
  if (['male', 'm', 'man', 'men', 'gentleman', 'gentlemen'].includes(g)) return 'Male';
  return null;
}
